import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _throttle from 'lodash/throttle';
import SmoothCollapse from 'react-smooth-collapse';
import ChevronDownIcon from 'calcite-ui-icons-react/ChevronDownIcon';
import ReactTooltip from 'react-tooltip';
import { SketchPicker } from 'react-color';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import RangeWithInputs from '../range-with-inputs';
import LayerTooltip from '../layer-components/layer-tooltip';

import Menu from '../../Menu';

import MapController from '../../../../controllers/MapController';

import { RootState } from '../../../../reducers/app';
import { toggleLayerModal } from '../../../../reducers/mapview/actions';

import InfoSvg from '../../../../assets/info-icon';
import '../map-layers.scss';
import './my-layer.scss';
import DownloadMyLayer from './download-my-layer';

const MyLayer = ({ setOverflow, layer, provided, snapshot }: any) => {
  // LOCAL STATE
  const [thresholdValues, setThresholdValues] = useState<Array<number>>([]);
  const [min, setMin] = useState<number>(-Infinity);
  const [max, setMax] = useState<number>(Infinity);
  const [sectionOpen, setSectionOpen] = useState<boolean>(false);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [showOpacitySlider, setShowOpacitySlider] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string>('');

  const initColor = {
    hex: '#0022D9',
    rgb: { r: 1, g: 103, b: 167, a: 1 },
    hsl: { h: 0, s: 0, l: 0.2, a: 1 },
  };

  const initOpacity = 0.39;

  const [activeColor, setActiveColor] = useState(initColor);
  const [activeOpacity, setActiveOpacity] = useState(initOpacity);

  // REDUX
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);
  const thresholds = useSelector((state: RootState) => state.mapview.rasterThresholds);
  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const myLayers = useSelector((state: RootState) => state.mapview.myLayers);
  const auditAreaSize = useSelector((state: RootState) => state.auditdata.auditArea.size);
  const specificThreshold = thresholds[layer.id];

  const dispatch = useDispatch();

  const handleEditLayer = () => {
    dispatch(toggleLayerModal(true, layer.title));
  };

  const handleDeleteLayer = () => {
    dispatch(toggleLayerModal(true, layer.title, true));
    setDropdownVisible(false);
  };

  const revealColorPicker = () => {
    setShowColorPicker(true);
  };

  const revealOpacitySlider = () => {
    setShowOpacitySlider(true);
  };

  const downloadLayer = () => {
    MapController.downloadFeatureLayer(layer, layer.title, setDownloadUrl);
    setDropdownVisible(false);
  };

  const handleColor = (color: any) => {
    setActiveColor(color);
  };

  const handleChangeOpacityComplete = (color: any) => {
    setActiveOpacity(color);
  };

  useEffect(() => {
    if (!showColorPicker) return;
    const handleClick = (e: any) => {
      if (e.target.closest('.sketch-picker') || e.target.closest('.name-modal')) {
        // inside click
        return;
      }
      // outside click
      setShowColorPicker(false);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [showColorPicker]);

  useEffect(() => {
    if (!showOpacitySlider) return;
    const handleClick = (e: any) => {
      if (e.target.closest('.rc-slider-container') || e.target.closest('.name-modal')) {
        // inside click
        return;
      }
      // outside click
      setShowOpacitySlider(false);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [showOpacitySlider, showColorPicker]);

  useEffect(() => {
    const overflow = dropdownVisible || showColorPicker || showOpacitySlider ? 'overflow-visible' : '';
    setOverflow(overflow);
  }, [setOverflow, dropdownVisible, showOpacitySlider, showColorPicker]);

  useEffect(() => {
    if (JSON.stringify(activeColor) === JSON.stringify(initColor) && initOpacity === activeOpacity) return;
    const _renderer = {
      color: [activeColor.rgb.r, activeColor.rgb.g, activeColor.rgb.b, activeOpacity],
      outline: {
        color: activeColor!.hex,
        width: 2,
      },
    };
    MapController.setLayerDesign(layer, _renderer);
  }, [activeColor, activeOpacity, initColor, layer]);

  useEffect(() => {
    if (specificThreshold && (specificThreshold.min !== min || specificThreshold.max !== max)) {
      setThresholdValues(specificThreshold.thresholdValues);
      setMin(specificThreshold.min);
      setMax(specificThreshold.max);
    }
    if (specificThreshold && specificThreshold.count === layer.layers.items.length) {
      MapController.resetMinMax();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds]);

  useEffect(() => {
    if (thresholdValues?.length === 2) {
      MapController.updateGroupRasterThreshold(thresholdValues, layer.id);
    }
  }, [thresholdValues, layer.id]);

  useEffect(() => {
    if (
      layer.id.includes('raster-analysis') &&
      myLayers[myLayers.length - 1].id.includes('raster-analysis') &&
      !loaderActive &&
      !sectionOpen
    ) {
      setSectionOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderActive]);

  const throttledSetThresholdValues = _throttle(setThresholdValues, 250, { trailing: true });

  const area = () => {
    let area;
    if (layer.id.includes('auditArea') && layer.graphics.length > 0) {
      area = auditAreaSize;
    } else if (!layer.id.includes('raster-analysis')) {
      if (layer.graphics) {
        area = layer.id.includes('riparian-boundary') ? layer.area : layer.graphics.items[0].attributes.area;
      } else if (layer.type === 'feature') {
        area = layer.area || 0;
      }
    }
    if (area) {
      return area.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      return null;
    }
  };

  const opacityMarks = {
    0: '0',
    0.25: '25',
    0.5: '50',
    0.75: '75',
    1: '100',
  };

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    userSelect: 'none',
    border: isDragging ? '2px solid #0093b2' : '',
    boxSizing: isDragging ? 'border-box' : '',
    ...draggableStyle,
  });

  const dropdownList = [
    { icon: 'esri-icon-edit', onClick: handleEditLayer, label: 'Edit Label' },
    { icon: 'esri-icon-environment-settings', onClick: revealColorPicker, label: 'Edit Color' },
    { icon: 'esri-icon-hollow-eye', onClick: revealOpacitySlider, label: 'Opacity' },
    { icon: 'esri-icon-download', onClick: downloadLayer, label: 'Download' },
    { icon: 'esri-icon-trash', onClick: handleDeleteLayer, label: 'Delete' },
  ];

  return (
    <div
      id={layer.title}
      className='layer-container'
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
    >
      <div className='layer'>
        <div className='layer-title__wrapper'>
          <span className='esri-icon-handle-vertical' aria-label='close icon' {...provided.dragHandleProps}></span>
          <input
            type='checkbox'
            id={layer.id}
            name={layer.id}
            checked={visibleLayers.includes(layer.id)}
            onChange={() => MapController.updateLayerVisibility(layer.id, !visibleLayers.includes(layer.id))}
          />
          <div
            className={`layer-title ${layer.id.includes('raster-analysis') ? 'veg-index' : ''} `}
            onClick={() => setSectionOpen(layer.type !== 'feature' ? !sectionOpen : false)}
          >
            {layer.title}
          </div>
        </div>
        <div className='layer-actions'>
          <span className='esri-icon-drag-horizontal' onClick={() => setDropdownVisible(!dropdownVisible)} />
          {layer.type !== 'feature' && <ChevronDownIcon size={24} onClick={() => setSectionOpen(!sectionOpen)} />}
        </div>

        {dropdownVisible && (
          <div className='myLayers'>
            <Menu
              menuItems={dropdownList}
              setDropdownVisible={setDropdownVisible}
              isFeatureService={layer.type === 'feature' && layer.title !== 'Audit Area'}
            />
          </div>
        )}
        {showColorPicker && (
          <SketchPicker disableAlpha={true} color={activeColor!.hex} onChangeComplete={handleColor} />
        )}
        {showOpacitySlider && (
          <div className='rc-slider-container'>
            <div className='absolute-parent'>
              <h5>Opacity</h5>
              <Slider
                min={0}
                max={1}
                marks={opacityMarks}
                step={0.01}
                onChange={handleChangeOpacityComplete}
                defaultValue={0.39}
              />
              <div className='opacity-marks'>
                <p>|</p>
                <p>|</p>
                <p>|</p>
                <p>|</p>
                <p>|</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <SmoothCollapse className='threshold-container' expanded={sectionOpen} allowOverflowWhenOpen>
        {!layer.id.includes('raster-analysis') && (
          <div className='total-area'>
            <div className='title'>Total Area (ha)</div>
            <div className='area'>{area()}</div>
          </div>
        )}
        {layer.id.includes('raster-analysis') && (
          <React.Fragment>
            <div className='threshold-legend'>
              <div className='legend-item'>
                <svg width='14' height='14'>
                  <rect width='14' height='14' fill='rgba(255, 0, 0, 1)' />
                </svg>
                <div className='legend__name'>Vegetation Loss</div>
              </div>
              <div className='legend-item'>
                <svg width='14' height='14'>
                  <rect width='14' height='14' fill='rgba(0, 255, 0, 1)' />
                </svg>
                <div className='legend__name'>Vegetation Gain</div>
              </div>
            </div>
            <RangeWithInputs
              layer={layer}
              rangeValues={thresholdValues}
              min={min}
              max={max}
              onChangeFunction={throttledSetThresholdValues}
              step={0.01}
            />

            <div className='component-name'>
              Adjust the sliders to isolate areas with the highest gain/loss probability
              <LayerTooltip
                tip={
                  'This change image was created by subtracting the after image from the before image. Areas with more change indicate a larger difference between the two images. Adjust the slider to view areas with different amounts of change detected. More change indicates a greater amount of Loss or Gain was measured at that pixel.'
                }
                id={`tooltip-${layer.id}`}
              >
                <InfoSvg className='criterion-info' />
              </LayerTooltip>
            </div>
            <ReactTooltip
              className='tooltip'
              id={`tooltip-${layer.id}`}
              place='right'
              multiline={true}
              type='success'
              effect='solid'
              clickable={true}
            />
          </React.Fragment>
        )}
      </SmoothCollapse>
      {downloadUrl !== '' && <DownloadMyLayer url={downloadUrl} setDownloadUrl={setDownloadUrl} />}
    </div>
  );
};

export default MyLayer;
